// The same round trip as the scripts/xeenia-demo.mjs command line version.
import { signedRequest } from './signing';

const PREFIX = '/api/v1/integration/xeenia';

export const stepDefinitions = [
    { key: 'catalog', label: 'Katalógus lekérése' },
    { key: 'list', label: 'Referencialista lekérése' },
    { key: 'create', label: 'Terápia létrehozása' },
    { key: 'read', label: 'Terápia visszaolvasása' },
    { key: 'patch', label: 'Terápia módosítása' },
    { key: 'lifecycle', label: 'Életciklus lépés' },
];

function isoDate(offsetDays) {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);

    return d.toISOString().slice(0, 10);
}

function approvingDoctor() {
    return {
        external_id: 'XEENIA-DEMO-DOC-1',
        name: 'Dr. Demo Orvos',
        approved_at: new Date().toISOString(),
    };
}

// Finds a usable value for each required parameter from the catalog description.
export function parametersFor(parameters) {
    const params = {};

    for (const field of parameters || []) {
        if (field.required !== true) {
            continue;
        }

        if (field.example !== undefined && field.example !== null) {
            params[field.key] = field.example;
            continue;
        }

        switch (field.type) {
            case 'integer':
            case 'number':
            case 'target_value':
                params[field.key] = field.min ?? 1;
                break;
            case 'choice':
                params[field.key] = field.options?.[0]?.value ?? '';
                break;
            case 'boolean':
                params[field.key] = true;
                break;
            default:
                params[field.key] = 'demo';
        }
    }

    return params;
}

// signedRequest answers text; this round needs the response JSON as well.
async function call(ctx, method, path, body) {
    const rawBody = body === undefined || body === null ? '' : JSON.stringify(body);
    const res = await signedRequest({
        baseUrl: ctx.baseUrl,
        token: ctx.token,
        secret: ctx.secret,
        method,
        path,
        rawBody,
    });

    let json = null;
    try {
        json = JSON.parse(res.body);
    } catch (e) {
        json = null;
    }

    return { ...res, json, request: `${method} ${path}` };
}

export async function preflight(ctx) {
    try {
        const res = await call(ctx, 'GET', `${PREFIX}/catalog`);

        if (res.status === 401 || res.status === 403) {
            return {
                ok: false,
                message: 'A backend fut, de a token vagy az aláíró kulcs nem jó. '
                    + 'Nézd meg a Beállítások blokkot és az apps/api/.env fájlt.',
            };
        }

        return { ok: true, response: res };
    } catch (e) {
        return { ok: false, message: null, networkError: String(e) };
    }
}

// The `report` callback runs at the start and the end of every step.
export async function runRoundTrip(ctx, report, preloadedCatalog = null) {
    const externalRef = ctx.externalRef;
    const ref = encodeURIComponent(externalRef);

    // 1. Catalog
    report('catalog', { state: 'running' });
    const catalog = preloadedCatalog || await call(ctx, 'GET', `${PREFIX}/catalog`);
    const components = Array.isArray(catalog.json?.components) ? catalog.json.components : [];
    const component = components.find((c) => c.component_version_id) || null;

    if (catalog.status !== 200 || !component) {
        report('catalog', {
            state: 'fail',
            ...catalog,
            note: catalog.status !== 200
                ? 'A katalógus lekérése nem sikerült.'
                : 'A katalógusban nincs publikált komponensverzió. Futtasd a demó seedert: php artisan db:seed --class=DemoSeeder',
        });

        return { finished: false };
    }

    report('catalog', {
        state: 'ok',
        ...catalog,
        note: `${components.length} publikált komponens. Választott: "${component.name}" `
            + `(component_version_id=${component.component_version_id})`,
    });

    // 2. Reference list, starting with the one the chosen component parameter points at.
    report('list', { state: 'running' });
    const listRef = (component.parameters || []).find((f) => f.value_source?.kind === 'list');
    const listKey = listRef?.value_source?.key || catalog.json?.lists?.[0]?.key || null;

    if (!listKey) {
        report('list', {
            state: 'skip',
            note: 'A katalógus egyetlen referencialistát sem hirdet, ezt a lépést kihagytuk.',
        });
    } else {
        const list = await call(ctx, 'GET', `${PREFIX}/lists/${encodeURIComponent(listKey)}`);
        report('list', {
            state: list.status === 200 ? 'ok' : 'fail',
            ...list,
            note: list.status === 200
                ? `"${list.json?.name}" lista, ${list.json?.item_count} elem.`
                : `A(z) "${listKey}" lista lekérése nem sikerült.`,
        });
    }

    // 3. Create
    report('create', { state: 'running' });
    const payload = {
        schema_version: '1.0',
        external_ref: externalRef,
        patient: { xmed_master_patient_id: ctx.patientId },
        approving_doctor: approvingDoctor(),
        therapy: {
            starts_on: isoDate(0),
            ends_on: isoDate(13),
            timezone: 'Europe/Budapest',
        },
        components: [
            {
                component_version_id: component.component_version_id,
                parameters: parametersFor(component.parameters),
                daily_frequency: 1,
                time_of_day: 'anytime',
                weekdays: [1, 3, 5],
            },
        ],
    };

    const created = await call(ctx, 'POST', `${PREFIX}/therapies`, payload);

    if (created.status !== 201 && created.status !== 200) {
        report('create', {
            state: 'fail',
            ...created,
            note: created.status === 404
                ? `A(z) "${ctx.patientId}" paciens nem talalhato. Futtasd a demo seedert: php artisan db:seed --class=DemoSeeder`
                : 'A terápia létrehozása nem sikerült, a válasz törzse lent olvasható.',
        });

        return { finished: false };
    }

    report('create', {
        state: 'ok',
        ...created,
        note: created.status === 201
            ? `Létrejött, ${created.json?.tasks_generated ?? '?'} feladat generálva. external_ref=${externalRef}`
            : 'Idempotens ismétlés, a már létező terápia jött vissza.',
    });

    // 4. Read back by the partner's own reference
    report('read', { state: 'running' });
    const status = await call(ctx, 'GET', `${PREFIX}/therapies/${ref}`);
    report('read', {
        state: status.status === 200 ? 'ok' : 'fail',
        ...status,
        note: status.status === 200
            ? `Állapot: ${status.json?.status}, adherencia: ${status.json?.compliance_percentage}%`
            : 'A terápia visszaolvasása nem sikerült.',
    });

    if (status.status !== 200) {
        return { finished: false };
    }

    // 5. Update: one week shorter
    report('patch', { state: 'running' });
    const patched = await call(ctx, 'PATCH', `${PREFIX}/therapies/${ref}`, {
        schema_version: '1.0',
        approving_doctor: approvingDoctor(),
        therapy: { ends_on: isoDate(6) },
    });
    report('patch', {
        state: patched.status === 200 ? 'ok' : 'fail',
        ...patched,
        note: patched.status === 200
            ? `Új zárodátum: ${patched.json?.ends_on}, feladatok: ${patched.json?.tasks?.total}`
            : 'A módosítás nem sikerült.',
    });

    if (patched.status !== 200) {
        return { finished: false };
    }

    // 6. Lifecycle
    report('lifecycle', { state: 'running' });
    const lifecycle = await call(ctx, 'POST', `${PREFIX}/therapies/${ref}/lifecycle`, {
        schema_version: '1.0',
        action: ctx.lifecycleAction || 'abort',
        reason: 'Demó lezárás.',
    });
    report('lifecycle', {
        state: lifecycle.status === 200 ? 'ok' : 'fail',
        ...lifecycle,
        note: lifecycle.status === 200
            ? `Állapot: ${lifecycle.json?.status}`
            : 'Az életciklus lépés nem sikerült.',
    });

    return { finished: lifecycle.status === 200, externalRef };
}
